import { Analytics } from 'Shared/utils/client';
import { LINK } from '@constants';
import { Accordion, Button, Column, Divider, Icon, Point, Row, Typography } from '@viewup/ui';
import { useGetTrend } from 'app/viewing-money/_features/api';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import type { GetPointCountResponseDataDto } from 'viewup-api/v2';
import { todayPointLists } from './PointInformation.const';

interface PointInformationProps {
  data?: GetPointCountResponseDataDto;
  pendingPoint?: number;
}

export const PointInformation = ({ data, pendingPoint }: PointInformationProps) => {
  const router = useRouter();
  const currentYear = dayjs().year();
  const currentMonth = dayjs().month() + 1;

  // 임시처리 - Trend API로 오늘 모은 포인트 적용 [ 시스템, 어드민 포인트 카테고리 제외를 위한 적용(다른 API는 다 포함) ]
  const { data: todayPoint } = useGetTrend({
    query: {
      monthValue: currentMonth,
      type: 'day',
      yearValue: currentYear,
    },
  });

  return (
    <Column bgColor='bg.primary' br={16} gap={16} p={16} shadow='center.normal'>
      <Column gap={4}>
        <Typography color='text.tertiary' fontType='body2.regular'>
          현재 보유 포인트
        </Typography>
        <Row alignItems='center' justifyContent='space-between'>
          <Row alignItems='center' gap={4}>
            <Icon color='icon.point' size='S' source={Point} />
            <Typography fontType='header.bold'>
              {data?.exchangeablePoint?.toLocaleString()}
            </Typography>
          </Row>
          <Button
            onClick={() => {
              Analytics.getInstance().track('VIEWING_MONEY_HISTORY');
              router.push(LINK.VIEWING_MONEY_LIST);
            }}
            size={'M'}
            variant='secondarySolid.info'
          >
            내역보기
          </Button>
        </Row>
        <Row
          alignItems='center'
          bgColor='bg.primary'
          border='1 solid border.info'
          br={999}
          css={{ alignSelf: 'flex-start', paddingBlock: 6 }}
          gap={4}
          mt={4}
          px={8}
        >
          <Typography color='text.info' css={{ lineHeight: '18px' }} fontType='body2.regular'>
            적립 예정
          </Typography>
          <Typography color='text.info' css={{ lineHeight: '18px' }} fontType='body2.bold'>
            {`+${Math.max(pendingPoint ?? 0, 0).toLocaleString()}P`}
          </Typography>
        </Row>
      </Column>
      <Divider bgColor='border.tertiary' />
      <Accordion
        defaultValue='today'
        items={[
          {
            content: (
              <Column bgColor='bg.secondary' br={8} gap={12} mt={8} p={16}>
                {todayPointLists.map((list) => (
                  <Row justifyContent='space-between' key={list.value} width='100%'>
                    <Typography color='text.tertiary' fontType='body2.regular'>
                      {list.label}
                    </Typography>
                    <Typography color='text.secondary' fontType='body2.medium'>
                      {data?.pointsCollectedTodayCategory.find(
                        (item) => item.category === list.value,
                      )?.pointsCollectedToday || 0}
                      P
                    </Typography>
                  </Row>
                ))}
              </Column>
            ),
            trigger: (
              <Row alignItems='center' justifyContent='space-between' width='100%'>
                <Row alignItems='center' gap={4}>
                  <Icon color='icon.point' size='XS' source={Point} />
                  <Typography color='text.primary' fontType='body2.regular'>
                    오늘 모은 포인트
                  </Typography>
                </Row>
                <Typography fontType='header3.medium' mr={4}>
                  {todayPoint?.data
                    .find((item) => dayjs(item.pointsCollectedDate).date() === dayjs().date())
                    ?.pointsCollected.toLocaleString() || 0}
                  P
                </Typography>
              </Row>
            ),
            value: 'today',
          },
        ]}
        size='M'
        styledComponents={{
          trigger: {
            p: 0,
          },
        }}
      ></Accordion>
    </Column>
  );
};
